import { useSubmit } from "@certn/api";
import { useCallback } from "react";

import { useAuth } from "./useAuth";
import { extractConnectionName, isSsoUser, SsoUserInfo } from "../utils";

export type LogoutType = "user_initiated" | "session_timeout" | "token_expired";

export interface UseLogoutOptions {
  /**
   * The type of logout event for audit logging.
   * @default "user_initiated"
   */
  logoutType?: LogoutType;
  /**
   * Whether to clear localStorage before logout.
   * @default true
   */
  clearLocalStorage?: boolean;
}

export interface UseLogoutResult {
  /**
   * Performs logout with proper cleanup and federated logout for SSO users.
   */
  logout: (options?: UseLogoutOptions) => Promise<void>;
}

/**
 * Hook that provides a centralized logout function with:
 * - Audit logging for SOC 2 compliance
 * - Federated logout support for SSO users (terminates IdP session)
 * - localStorage cleanup
 * - Consistent logout behavior across the application
 *
 * @example
 * ```tsx
 * const { logout } = useLogout();
 *
 * // User-initiated logout (default)
 * await logout();
 *
 * // Token expired (e.g. from SessionTimeoutMonitor)
 * await logout({ logoutType: "token_expired" });
 * ```
 */
export function useLogout(): UseLogoutResult {
  const { logout: auth0Logout, user } = useAuth();
  const { submit: submitLogoutLog } = useSubmit(
    "/api/internal/self/data/log-user-logout/",
    { throwErrors: true }
  );

  const logout = useCallback(
    async (options: UseLogoutOptions = {}) => {
      const { logoutType = "user_initiated", clearLocalStorage = true } =
        options;

      // Cast user to SsoUserInfo to access custom claims for linked SSO users
      const userInfo = user as SsoUserInfo | undefined;
      const isSso = isSsoUser(userInfo);
      const connectionName = extractConnectionName(userInfo);

      // Log the logout event for SOC 2 audit compliance
      // Must await to ensure request completes before browser redirects to Auth0
      // Without await, the redirect would cancel the in-flight request
      try {
        await submitLogoutLog({
          sub: user?.sub,
          email: user?.email,
          logout_type: logoutType,
          connection_name: connectionName,
          federated: isSso,
        });
      } catch (error) {
        // Log error but don't block logout - user experience takes priority
        console.error("Failed to log logout event:", error);
      }

      // Clear local storage before Auth0 logout
      if (clearLocalStorage) {
        localStorage.clear();
      }

      // Perform Auth0 logout with federated logout for SSO users
      // federated: true tells Auth0 to also terminate the IdP session
      await auth0Logout({
        logoutParams: {
          returnTo: window.location.origin,
          ...(isSso && { federated: true }),
        },
      });
    },
    [auth0Logout, user, submitLogoutLog]
  );

  return { logout };
}
